import { useTranslation } from '@exam-room/translations';
import { cn } from '@exam-room/utils';
import { useDisclosure } from '@shared/hooks';
import { useEffect, useState } from 'react';
import { matchPath, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  AddOutlinedIcon,
  Button,
  DesignServicesOutlinedIcon,
  ExamExitModal,
  IconButton,
  LanguageSwitchMenu,
  RemoveOutlinedIcon,
  Tooltip,
} from '../../../components';
import { NetworkAlert } from '../../../containers';
import { useGetCookie } from '../../../hooks';
import { useConfig, useExamEvent, useNewExam } from '../../../providers';
import { PATHS } from '../../../routes/constants';
import { ExamTools, TimerAlert } from '../containers';
import { useFontSize } from '../contexts/FontSizeContext';

import ExamFooter from './ExamFooter';
import ExamHeader from './ExamHeader';

function ExamLayoutContent({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { cookie } = useConfig();
  const { t, i18n } = useTranslation();
  const { language, changeLanguage } = i18n;
  const [isExitModalOpen, { close: closeExitModal }] = useDisclosure(false);
  const [searchParams] = useSearchParams({});
  const { questions, currentExamOrder, attachDocumentUrl } = useNewExam();
  const [showExamTools, setShowExamTools] = useState(true);
  const q = searchParams.get('q') ?? '';
  const page = searchParams.get('page') ?? '';
  const isExamRoomPage = page !== 'review' && q !== '';
  const isReviewPage = page === 'review';
  const sessionCookie = useGetCookie(cookie.examSessionCookieName);

  const { increaseFontSize, decreaseFontSize, fontScale } = useFontSize();

  const shouldHideTimer = [
    PATHS.Exam.Questionnaire,
    PATHS.Exam.Completed,
    PATHS.Exam.Submitted,
  ].some((path) => matchPath(path, location.pathname));

  useEffect(() => {
    // TODO: Remove after can check multiple doing exam from API
    // TODO: SET eExam - check this
    if (!sessionCookie && !(import.meta.env.MODE === 'development')) {
      // navigate(PATHS.SessionExpired);
    }
  }, [q]);

  const menus: JSX.Element[] = [
    // Font Size Adjustment
    <div key='font-size-adjust' className='flex items-center gap-1'>
      <Tooltip content={t('header.fontSizeDecrease')} position='bl' className='z-50 font-semibold'>
        <IconButton onClick={decreaseFontSize} disabled={fontScale === -1}>
          <RemoveOutlinedIcon />
        </IconButton>
      </Tooltip>
      <Tooltip content={t('header.fontSize')} position='bl' className='z-50 font-semibold'>
        <div className='text-body1 bg-grey-100 grid size-10 place-items-center rounded-sm px-3 py-2 font-semibold'>
          A
        </div>
      </Tooltip>
      <Tooltip content={t('header.fontSizeIncrease')} position='bl' className='z-50 font-semibold'>
        <IconButton onClick={increaseFontSize} disabled={fontScale === 1}>
          <AddOutlinedIcon />
        </IconButton>
      </Tooltip>
      <LanguageSwitchMenu
        key='language-switch-menu'
        currentLanguage={language.toUpperCase()}
        onSwitchToEnglish={() => {
          changeLanguage('en');
        }}
        onSwitchToThai={() => {
          changeLanguage('th');
        }}
      />
    </div>,

    ...(isExamRoomPage
      ? [
          <Button
            key='exam-tools-button'
            variant='outlined'
            className={cn(
              'px-3 py-2',
              showExamTools && 'bg-warning-100 border-warning-700 text-warning-700',
            )}
            onClick={() => setShowExamTools(!showExamTools)}
          >
            <div className='flex items-center gap-2 font-semibold'>
              <DesignServicesOutlinedIcon
                className={cn('text-xl', showExamTools && 'text-warning')}
              />
              {t('header.examTools')}
            </div>
          </Button>,
        ]
      : []),
    // <ExamMenu
    //   key='exam-menu'
    //   icon={EllipsisVerticalIcon}
    //   menu={[{ icon: ArrowLeftOnRectangleIcon, label: 'Exit exam room', onClick: openExitModal }]}
    // />,
  ];

  return (
    <>
      <ExamExitModal
        open={isExitModalOpen}
        title='Do You Want To Exit the Exam?'
        description={`Testing time will keep counting down while you're away.`}
        disabledCheckBox={false}
        onClose={closeExitModal}
        onExit={() => navigate(PATHS.Home)}
        exitText='Exit'
      />

      <ExamHeader menus={menus} withTimer={!shouldHideTimer} />
      <div className='flex flex-1 flex-col overflow-auto p-6'>
        <NetworkAlert />
        <TimerAlert />
        <div
          className={cn(
            'flex flex-1 flex-row',
            showExamTools && isExamRoomPage && 'gap-6',
            isReviewPage && 'gap-6',
          )}
        >
          <div
            className={cn(
              'transition-all duration-300',
              showExamTools && isExamRoomPage ? 'w-[calc(100%-364px)]' : 'w-full',
            )}
          >
            {children}
          </div>
          {isExamRoomPage ? (
            <div className='flex-1'>
              <ExamTools
                questions={questions ?? []}
                currentExamOrder={currentExamOrder}
                show={showExamTools}
                onClose={() => setShowExamTools(false)}
                url={attachDocumentUrl}
              />
            </div>
          ) : null}
        </div>
      </div>
      {isExamRoomPage ? <ExamFooter /> : null}
    </>
  );
}

export default function ExamLayout(props: { children: React.ReactNode }) {
  return <ExamLayoutContent {...props} />;
}
